<template>
  <div id="business-login-form" class="box auth">
    <h1 class="title has-text-centered">
      <span v-if="signupForm.show || mfa.mode === 'SETUP'">{{
        $t('login_form.complete_setup')
      }}</span>
      <span v-else>
        {{ $t('login_form.title') }}
        <span class="has-text-weight-medium">{{ $t('navbar.title') }}</span>
      </span>
    </h1>

    <div
      class="message is-small is-danger animated fast fadeIn"
      v-if="errorMessage"
    >
      <div class="message-body">{{ errorMessage }}</div>
    </div>
    <div v-if="!mfa.mode && !signupForm.show" @keyup.enter="login()">
      <div class="field">
        <label for="business-email" class="label">{{
          $t('login_form.email_label')
        }}</label>
        <div class="control">
          <input
            type="email"
            id="business-email"
            class="input is-shadowless"
            autocomplete="email"
            required
            autofocus
            v-model="username"
          />
        </div>
      </div>

      <div class="field">
        <label for="business-password" class="label">{{
          $t('login_form.password_label')
        }}</label>
        <div class="control">
          <input
            type="password"
            id="business-password"
            class="input is-shadowless"
            autocomplete="current-password"
            required
            v-model="password"
          />
        </div>
      </div>

      <div class="field">
        <div class="control has-text-right">
          <router-link
            :to="{
              query: { tab: 'business' },
              name: 'reset-password-request',
            }"
            class="has-link"
            >{{ $t('login_form.forgot_password_link') }}</router-link
          >
        </div>
      </div>

      <div class="field">
        <div class="control">
          <gradient-button
            :title="$t('login_form.login_button')"
            :is-loading="isLoading"
            @click.native="login()"
          ></gradient-button>
        </div>
      </div>
    </div>

    <div v-if="mfa.mode" @keyup.enter="submitCode()">
      <div class="field has-text-centered" v-if="mfa.mode === 'SETUP'">
        <div class="help mfa-help">
          <span>{{ $t('login_form.mfa_scan_code') }}</span>
          <div class="tooltip-container">
            <i class="material-icons">&#xE88F;</i>
            <div class="tooltip tooltip-bottom" role="tooltip">
              <div class="tooltip-outer">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-inner">
                  {{ $t('login_form.mfa_help_text') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <QrCode :value="mfa.qrValue" />
      </div>
      <div class="field">
        <label for="mfa-code" class="label"
          >{{ $t('login_form.mfa_code_label') }}
          <div class="tooltip-container" v-if="mfa.mode !== 'SETUP'">
            <i class="material-icons">&#xE88F;</i>
            <div class="tooltip tooltip-top" role="tooltip">
              <div class="tooltip-outer">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-inner">
                  {{ $t('login_form.mfa_help_text_2') }}
                </div>
              </div>
            </div>
          </div>
        </label>
        <div class="control">
          <input
            type="text"
            id="mfa-code"
            name="mfa-code"
            class="input is-shadowless"
            autocomplete="off"
            required
            autofocus
            v-model="mfa.code"
          />
        </div>
      </div>

      <div class="field" v-if="mfa.mode === 'SUBMIT'">
        <div class="control has-text-right">
          <label for="remember-device" class="checkbox">
            <input
              type="checkbox"
              id="remember-device"
              class="checkbox-input"
              v-model="mfa.rememberDevice"
            />
            Remember device
          </label>
        </div>
      </div>

      <div class="field">
        <div class="control">
          <gradient-button
            title="Submit"
            :is-loading="isLoading"
            @click.native="submitCode()"
          ></gradient-button>
        </div>
      </div>

      <div class="field">
        <div class="control has-text-centered">
          <button class="button-link has-link" @click="goBack()">
            {{ $t('login_form.go_back') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="signupForm.show" @keyup.enter="completeSignup()">
      <div class="field" v-if="!firstName">
        <label for="business-firstname" class="label">{{
          $t('login_form.first_name_label')
        }}</label>
        <div class="control">
          <input
            type="email"
            id="business-firstname"
            class="input is-shadowless"
            required
            autofocus
            v-model="signupForm.firstName"
          />
        </div>
      </div>
      <div class="field" v-if="!lastName">
        <label for="business-lastname" class="label">{{
          $t('login_form.last_name_label')
        }}</label>
        <div class="control">
          <input
            type="email"
            id="business-lastname"
            class="input is-shadowless"
            required
            v-model="signupForm.lastName"
          />
        </div>
      </div>
      <div class="field">
        <label for="business-new-password" class="label">
          <span>{{ $t('login_form.new_password_label') }}</span>
          <div class="tooltip-container">
            <i class="material-icons">&#xE88F;</i>
            <div class="tooltip tooltip-top" role="tooltip">
              <div class="tooltip-outer">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-inner">
                  {{ $t('login_form.business_password_strength') }}
                </div>
              </div>
            </div>
          </div></label
        >
        <div class="control">
          <input
            type="password"
            id="business-new-password"
            class="input is-shadowless"
            required
            v-model="signupForm.newPassword"
          />
        </div>
      </div>

      <div class="field">
        <label for="busniess-confirm-password" class="label">{{
          $t('login_form.confirm_password_label')
        }}</label>
        <div class="control">
          <input
            type="password"
            id="busniess-confirm-password"
            class="input is-shadowless"
            required
            v-model="signupForm.confirmPassword"
          />
        </div>
      </div>

      <div class="field">
        <div class="control">
          <gradient-button
            title="Submit"
            :is-loading="isLoading"
            @click.native="completeSignup()"
          ></gradient-button>
        </div>
      </div>

      <div class="field">
        <div class="control has-text-centered">
          <button class="button-link has-link" @click="goBack()">
            {{ $t('login_form.go_back') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { get } from 'lodash';
import { mapState } from 'vuex';
import Auth from 'services/Cognito';
import QrCode from 'components/auth/QrCode';
import { configs } from 'config';
import { GradientButton } from 'components/buttons';
import { rememberDevice } from 'services/BusinessAuth';
import { isPasswordValid } from 'utils/auth';

export default {
  name: 'BusinessUserLogin',
  components: {
    GradientButton,
    QrCode,
  },
  data() {
    return {
      user: null,
      username: null,
      password: null,
      isLoading: false,
      errorMessage: '',
      mfa: {
        mode: null,
        qrValue: null,
        code: null,
        rememberDevice: false,
      },
      signupForm: {
        show: false,
        firstName: null,
        lastName: null,
        newPassword: null,
        confirmPassword: null,
      },
    };
  },

  computed: {
    firstName() {
      return get(this, 'user.challengeParam.userAttributes.given_name');
    },
    lastName() {
      return get(this, 'user.challengeParam.userAttributes.family_name');
    },
  },

  methods: {
    /**
     * Begins the login process. Based on what state the user is in (newly created, newly created but signup not
     * complete, newly created but not yet set up MFA, signup complete), a further prompt is show to either complete
     * signup, complete MFA setup, or enter TOTP.
     *
     * If the current device is remembered (from a previous login), it is also possible for login to complete with
     * the calling of this function.
     *
     * @returns {Promise<void>}
     */
    async login() {
      try {
        if (this.isLoading) {
          return;
        }

        if (this.username === '' || this.password === '') {
          this.errorMessage = this.$t(
            'login_form.errors.username_or_password_empty'
          );

          return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        this.user = await Auth.signIn(this.username, this.password);

        // login complete
        if (!this.user.challengeName) {
          await this.completeLogin();

          return;
        }

        // If newly created user, signup needs to be completed by setting new password,
        // and setting first and last names.
        if (this.user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          this.signupForm.show = true;

          return;
        }

        // If new user, who hasn't yet completed MFA setup, needs to complete MFA setup
        if (this.user.challengeName === 'MFA_SETUP') {
          await this.mfaSetup();

          return;
        }

        if (this.user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          this.mfa.mode = 'SUBMIT';
        }
      } catch (err) {
        this.errorMessage = err.message;
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      if (this.signupForm.show) {
        this.signupForm.show = false;
        this.errorMessage = '';
      }
      if (this.mfa.mode) {
        this.mfa.mode = '';
        this.errorMessage = '';
      }
    },

    async submitCode() {
      if (this.isLoading) {
        return;
      }

      if (!this.mfa.code) {
        this.errorMessage = this.$t('login_form.errors.mfa_empty');

        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        // Submitted TOTP can be either for initial MFA setup, or subsequent
        // MFA submission. Either case will lead to returning tokens.
        // Move to storing tokens and completing login, after.
        if (this.user.challengeName === 'MFA_SETUP') {
          await Auth.verifyTotpToken(this.user, this.mfa.code);
        } else if (this.user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          this.user = await Auth.confirmSignIn(
            this.user,
            this.mfa.code,
            'SOFTWARE_TOKEN_MFA'
          );
        }

        await this.completeLogin();

        this.mfa.code = '';
      } catch (err) {
        console.log(err);
        if (err.code === 'EnableSoftwareTokenMFAException') {
          this.errorMessage = this.$t('login_form.errors.mfa_software_token');
        } else if (err.code === 'InvalidParameterException') {
          this.errorMessage = this.$t('login_form.errors.mfa_invalid_param');
        } else {
          this.errorMessage = err.message;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Submit new password and mandatory fields, completing the signup process.
     * Process moves into MFA setup, after.
     *
     * @returns {Promise<void>}
     */
    async completeSignup() {
      if (this.isLoading) {
        return;
      }

      const keys = ['newPassword', 'confirmPassword'];

      if (!this.firstName) {
        keys.push('firstName');
      }
      if (!this.lastName) {
        keys.push('lastName');
      }

      if (keys.filter(key => !this.signupForm[key]).length) {
        this.errorMessage = this.$t('login_form.errors.empty_field');
        return;
      }

      if (this.signupForm.newPassword !== this.signupForm.confirmPassword) {
        this.errorMessage = this.$t('login_form.errors.passwords_do_not_match');

        return;
      }

      if (!isPasswordValid(this.signupForm.newPassword)) {
        this.errorMessage = this.$t('login_form.business_password_strength');
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        this.user = await Auth.completeNewPassword(
          this.user,
          this.signupForm.newPassword,
          {
            given_name: this.signupForm.firstName || this.firstName,
            family_name: this.signupForm.lastName || this.lastName,
          }
        );
        this.signupForm.show = false;

        if (this.user.challengeName === 'MFA_SETUP') {
          await this.mfaSetup();
        }
      } catch (err) {
        console.log(err);
        this.errorMessage = err.message;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Get MFA secret and show user a QR code to start setup.
     *
     * @returns {Promise<void>}
     */
    async mfaSetup() {
      const totpSecret = await Auth.setupTOTP(this.user);
      const label = encodeURI(`${configs().TOTP_ISSUER}:${this.username}`);
      const keyUri = `otpauth://totp/${label}?secret=${totpSecret}&issuer=${
        configs().TOTP_ISSUER
      }`;

      this.mfa.mode = 'SETUP';
      this.mfa.qrValue = keyUri;
    },

    /**
     * Get tokens out of Cognito user object, store for usage by the local system.
     *
     * @returns {Promise<void>}
     */
    async completeLogin() {
      const userSession = await Auth.currentSession();
      const userId = userSession.idToken.payload['cognito:username'];
      const accessToken = userSession.accessToken.jwtToken;
      const deviceKey = localStorage.getItem(
        `CognitoIdentityServiceProvider.${
          configs().COGNITO_CLIENT_ID
        }.${userId}.deviceKey`
      );

      if (this.mfa.rememberDevice && accessToken && deviceKey) {
        await rememberDevice(accessToken, deviceKey);
      }

      this.$emit('login-success', {
        userId,
        token: userSession.idToken.jwtToken,
      });
    },
  },
};
</script>
